import LBox from "@components/LBox";
import LText from "@components/LText";
import { LevelConfig } from "@hooks/profile/useUserStats";
import React, { useMemo } from "react";
import Svg, { Path, Line } from "react-native-svg";
import {
  LevelBadge,
  levelShadowColors,
} from "@components/Account/You/LevelBadge";
import LGradient from "@components/LGradient";
import { formatMoneySpent } from "@utils/formatting/money";
import { useMe } from "@hooks/trpc/useMe";
import { DEFAULT_SCORING_CONFIG } from "@libs/gamification-rewards-contracts/scoring";
import type { YouBreakdownModalProps } from "./types";

// Format time from seconds — shows minutes when under 1 hour
const formatStreamTime = (seconds: number): string => {
  const hours = Math.floor(seconds / 3600);
  if (hours > 0) return `${hours} hrs`;
  const minutes = Math.floor(seconds / 60);
  return `${minutes} min`;
};

// Level progress component
interface LevelProgressProps {
  currentLevelName: string;
  containerHeight?: number;
  score: number;
  nextLevelName: string | null;
  scoringLevels: LevelConfig[];
}

// Helper to get background color with transparency from shadow color
const getLevelBackgroundColor = (
  shadowColor: string,
  alpha: number = 0.3,
): string => {
  const hex = shadowColor.replace("#", "");
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

// Blend a hex color towards white by a factor (0 = original, 1 = white)
const lightenColor = (hex: string, factor: number): string => {
  const h = hex.replace("#", "");
  const r = Math.round(
    parseInt(h.substring(0, 2), 16) +
      (255 - parseInt(h.substring(0, 2), 16)) * factor,
  );
  const g = Math.round(
    parseInt(h.substring(2, 4), 16) +
      (255 - parseInt(h.substring(2, 4), 16)) * factor,
  );
  const b = Math.round(
    parseInt(h.substring(4, 6), 16) +
      (255 - parseInt(h.substring(4, 6), 16)) * factor,
  );
  return `#${r.toString(16).padStart(2, "0")}${g.toString(16).padStart(2, "0")}${b.toString(16).padStart(2, "0")}`;
};

// Helper to find a level config by name
const findLevelByName = (
  levels: LevelConfig[],
  name: string,
): LevelConfig | undefined => {
  return levels.find((l) => l.name.toLowerCase() === name.toLowerCase());
};

// Arrow component using SVG for proper dotted line
const DottedArrowSvg = ({
  variant,
  width = 60,
}: {
  variant: "orange" | "split";
  width?: number;
}) => {
  const height = 12;
  const orangeColor = "#CD7F32";
  const greyColor = "#666666";
  const arrowSize = 6;

  if (variant === "orange") {
    return (
      <Svg width={width} height={height} viewBox={`0 0 ${width} ${height}`}>
        <Line
          x1={0}
          y1={height / 2}
          x2={width - arrowSize}
          y2={height / 2}
          stroke={orangeColor}
          strokeWidth={2}
          strokeDasharray="4,3"
        />
        <Path
          d={`M${width - arrowSize} ${height / 2 - arrowSize / 2} L${width} ${height / 2} L${width - arrowSize} ${height / 2 + arrowSize / 2} Z`}
          fill={orangeColor}
        />
      </Svg>
    );
  }

  // Split variant: orange first half, grey second half
  const midPoint = (width - arrowSize) / 2;
  return (
    <Svg width={width} height={height} viewBox={`0 0 ${width} ${height}`}>
      <Line
        x1={0}
        y1={height / 2}
        x2={midPoint}
        y2={height / 2}
        stroke={orangeColor}
        strokeWidth={2}
        strokeDasharray="4,3"
      />
      <Line
        x1={midPoint}
        y1={height / 2}
        x2={width - arrowSize}
        y2={height / 2}
        stroke={greyColor}
        strokeWidth={2}
        strokeDasharray="4,3"
      />
      <Path
        d={`M${width - arrowSize} ${height / 2 - arrowSize / 2} L${width} ${height / 2} L${width - arrowSize} ${height / 2 + arrowSize / 2} Z`}
        fill={greyColor}
      />
    </Svg>
  );
};

const LevelProgress = ({
  currentLevelName,
  containerHeight,
  score,
  nextLevelName,
  scoringLevels,
}: LevelProgressProps) => {
  const sortedLevels = useMemo(
    () => [...scoringLevels].sort((a, b) => a.minScore - b.minScore),
    [scoringLevels],
  );

  const currentLevel = findLevelByName(sortedLevels, currentLevelName);
  const currentIndex = currentLevel
    ? sortedLevels.findIndex((l) => l.level === currentLevel.level)
    : -1;

  const prevLevel = currentIndex > 0 ? sortedLevels[currentIndex - 1] : null;
  const nextLevel = nextLevelName
    ? findLevelByName(sortedLevels, nextLevelName)
    : null;

  const nextPoints = nextLevel?.minScore ?? null;
  const pointsToNext = nextPoints
    ? Math.round(Math.max(0, nextPoints - score))
    : null;
  const roundedScore = Math.round(score);

  const currentShadowColor =
    currentLevel?.shadowColor ??
    levelShadowColors[currentLevelName.toLowerCase()] ??
    "#808080";
  const prevShadowColor = prevLevel?.shadowColor ?? "#808080";

  const currentBgColor = getLevelBackgroundColor(currentShadowColor, 0.35);
  const prevBgColor = prevLevel
    ? getLevelBackgroundColor(prevShadowColor, 0.25)
    : "";

  return (
    <LBox
      flexDirection="row"
      justifyContent="space-between"
      {...(containerHeight ? { height: containerHeight } : { flex: 1 })}
    >
      <LBox width={56} alignItems="center">
        {prevLevel ? (
          <LBox alignItems="center" height="100%">
            <LBox height={80} justifyContent="center">
              <LevelBadge
                levelName={prevLevel.name}
                size="small"
                opacity={0.8}
                showBackground
                backgroundColor={prevBgColor}
                borderWidth={0}
                borderColor="transparent"
                shadowColor={prevLevel.shadowColor}
                phaseOffset={0}
                baseDuration={2800}
              />
            </LBox>
            <LText
              variant="Caption/C2/Medium"
              color="textPrimary"
              marginTop="s2"
            >
              {prevLevel.name}
            </LText>
            <LText
              variant="Caption/C2/Regular"
              color="subText"
              adjustsFontSizeToFit
              textAlign="center"
              numberOfLines={1}
            >
              {prevLevel.minScore}pts -{" "}
              {currentLevel?.minScore ?? prevLevel.minScore}pts
            </LText>
          </LBox>
        ) : null}
      </LBox>
      <LBox flex={1} alignItems="center" justifyContent="center" height={80}>
        {prevLevel && (
          <LBox width={50} alignItems="center" justifyContent="center">
            <DottedArrowSvg variant="orange" width={50} />
          </LBox>
        )}
      </LBox>
      <LBox alignItems="center">
        <LevelBadge
          levelName={currentLevelName}
          size="medium"
          showBackground
          backgroundColor={currentBgColor}
          borderWidth={0}
          borderColor="transparent"
          shadowColor={currentShadowColor}
          phaseOffset={0.5}
          baseDuration={3000}
        />
        <LBox marginTop="s2">
          <LGradient
            colors={[currentShadowColor, lightenColor(currentShadowColor, 0.7)]}
            gradientDirection="horizontal"
            paddingHorizontal="s1"
            paddingVertical="s1_5"
            borderTopLeftRadius="s"
            borderTopRightRadius="s"
            borderBottomLeftRadius={pointsToNext !== null ? "zero" : "s"}
            borderBottomRightRadius={pointsToNext !== null ? "zero" : "s"}
            alignItems="center"
            flexDirection="row"
            justifyContent="center"
            gap="s1"
          >
            <LText variant="Caption/C1/Bold" style={{ color: "black" }}>
              {roundedScore}
            </LText>
            <LText variant="Caption/C2/Regular" style={{ color: "black" }}>
              |
            </LText>
            <LText
              variant="Caption/C2/SemiBold"
              style={{ color: "black" }}
              adjustsFontSizeToFit
            >
              {currentLevelName.toUpperCase()}
            </LText>
          </LGradient>
          {pointsToNext !== null && (
            <LBox
              paddingHorizontal="s1"
              paddingVertical="s0_5"
              borderBottomLeftRadius="s"
              borderBottomRightRadius="s"
              style={{ backgroundColor: "rgba(65, 65, 65, 0.8)" }}
              alignItems="center"
            >
              <LText
                variant="Caption/C2/Regular"
                marginTop="s1"
                textAlignVertical="center"
              >
                {pointsToNext}pts to {nextLevelName}
              </LText>
            </LBox>
          )}
        </LBox>
      </LBox>
      <LBox flex={1} alignItems="center" justifyContent="center" height={80}>
        {nextLevel && (
          <LBox width={50} alignItems="center" justifyContent="center">
            <DottedArrowSvg variant="split" width={50} />
          </LBox>
        )}
      </LBox>
      <LBox width={56} alignItems="center">
        {nextLevel ? (
          <LBox alignItems="center" height="100%">
            <LBox height={80} justifyContent="center">
              <LevelBadge
                levelName={nextLevel.name}
                size="small"
                opacity={0.5}
                showBackground
                backgroundColor="rgba(60, 60, 60, 0.5)"
                borderWidth={0}
                borderColor="transparent"
                shadowColor={nextLevel.shadowColor}
                phaseOffset={1}
                baseDuration={2600}
              />
            </LBox>
            <LText
              variant="Caption/C2/Medium"
              color="textPrimary"
              marginTop="s2"
            >
              {nextLevel.name}
            </LText>
            <LText variant="Caption/C2/Regular" color="subText">
              {nextLevel.minScore}pts
            </LText>
          </LBox>
        ) : null}
      </LBox>
    </LBox>
  );
};

// Score breakdown section
interface ScoreBreakdownProps {
  streamTime: number;
  accesses: number;
  moneySpentStreaming: number;
  moneySpentD2C: number;
  currencySymbol?: string;
}

const ScoreBreakdown = ({
  streamTime,
  accesses,
  moneySpentStreaming,
  moneySpentD2C,
  currencySymbol = "£",
}: ScoreBreakdownProps) => {
  const stats = [
    { label: "Stream time", value: formatStreamTime(streamTime) },
    { label: "Experiences", value: String(accesses) },
    {
      label: "Money Spent",
      value: formatMoneySpent(
        moneySpentStreaming,
        moneySpentD2C,
        currencySymbol,
      ),
    },
  ];

  return (
    <LBox
      padding="s4"
      borderRadius="l"
      gap="s4"
      style={{
        backgroundColor: "rgba(40, 40, 40, 0.8)",
      }}
    >
      <LText variant="Caption/C1/Bold" color="subText">
        SCORE BREAKDOWN
      </LText>

      <LBox flexDirection="row" justifyContent="space-between">
        {stats.map((stat) => (
          <LBox key={stat.label} alignItems="center" flex={1}>
            <LText variant="Caption/C2/Regular" color="subText">
              {stat.label}
            </LText>
            <LText variant="Heading/H2/Bold" color="textPrimary" marginTop="s1">
              {stat.value}
            </LText>
          </LBox>
        ))}
      </LBox>
    </LBox>
  );
};

export const YouBreakdownContent = ({
  artistName,
  levelProgressHeight,
  score,
  levelName,
  nextLevelName,
  streamTime,
  accessesCount,
  moneySpentStreaming,
  moneySpentD2C,
}: YouBreakdownModalProps & { levelProgressHeight?: number }) => {
  const { query: meQuery } = useMe();
  const currencySymbol = meQuery.data?.plan?.currencySymbol ?? "£";
  const scoringLevels = DEFAULT_SCORING_CONFIG.levels;

  return (
    <LBox gap="m1" pt="s2">
      {/* Title */}
      <LText variant="Heading/H1/Bold">{artistName}&apos;s Fan Journey</LText>

      {/* Per-artist level progression */}
      <LevelProgress
        containerHeight={levelProgressHeight}
        currentLevelName={levelName}
        score={score}
        nextLevelName={nextLevelName}
        scoringLevels={scoringLevels}
      />

      {/* Score breakdown */}
      <ScoreBreakdown
        streamTime={streamTime}
        accesses={accessesCount}
        moneySpentStreaming={moneySpentStreaming}
        moneySpentD2C={moneySpentD2C}
        currencySymbol={currencySymbol}
      />
    </LBox>
  );
};
